import React, { useContext, useEffect, useState } from 'react';
import { PeFilterContext } from '../../Contexts/PeFilterContext';
import Popup from '../Common/Popup';

/**
 * The `SelectIndustryFilter` function is a React component that renders a dropdown menu for selecting
 * an industry filter option.
 * @returns a JSX element, which represents a filter component for selecting an industry.
 */
function SelectIndustryFilter({ IndustryOptions, setIndustryOptions, customPathname, label = "Industry", isPopupOpen, setIsPopupOpen }) {
    const { Industries, refineSearchFilters } = useContext(PeFilterContext);
    
    // Use internal state only if props are not provided (for backward compatibility)
    const [internalPopupOpen, setInternalPopupOpen] = useState(false);
    const popupOpen = isPopupOpen !== undefined ? isPopupOpen : internalPopupOpen;
    const setPopupOpen = setIsPopupOpen !== undefined ? setIsPopupOpen : setInternalPopupOpen;

    /* The `useEffect` hook is used to perform side effects in a React functional component. In this
    case, the `useEffect` hook is used to update the `IndustryOptions` state based on the values of
    `Industries`, `customPathname`, `refineSearchFilters`, and `setIndustryOptions`. */
    useEffect(() => {
        const industryIds = refineSearchFilters[customPathname]?.industry?.map(value => value.id) ?? [];
        setIndustryOptions(Industries?.map(industry => ({ ...industry, isChecked: industryIds.includes(industry.id) })));
    }, [Industries, customPathname, refineSearchFilters, setIndustryOptions]);

    /**
     * The function `handleInvestorTypeChange` updates the `isChecked` property of the
     * `IndustryOptions` array based on the selected value from the event target.
     * @param e - The parameter `e` is an event object that is passed to the `handleInvestorTypeChange`
     * function. It represents the event that triggered the function, such as a change event on an
     * input element.
     */
    const handleInvestorTypeChange = (e) => {
        const { value } = e.target;
        const currentIndustry = IndustryOptions.map((industry) =>
            ({ ...industry, isChecked: industry.id === parseInt(value) })
        );
        setIndustryOptions(currentIndustry);
    };

    const selectedIndustry = IndustryOptions?.filter(industry => industry.isChecked)?.at(0);

    const getDisplayText = () => {
        if (selectedIndustry?.name) {
            return `: ${selectedIndustry.name}`;
        }
        return "";
    };

    return (
        <div className="relative w-full md:inline-block">
            <button
                type="button"
                className="h-10 rounded-md px-4 py-1 text-gray-500 hover:bg-gray-100 text-sm shadow-md border border-gray-300 text-left flex items-center justify-between w-full md:w-[90%]"
                onClick={() => setPopupOpen(!popupOpen)}
            >
                <span className="truncate pr-2">{label}</span>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {selectedIndustry?.name && (
                        <span className="bg-white rounded-full px-2 py-0.5 text-xs font-medium border border-gray-400">
                            {getDisplayText()}
                        </span>
                    )}
                    <span className={`transition-transform duration-200 text-xl font-semibold ${popupOpen ? 'rotate-[270deg]' : 'rotate-90'}`}>
                        ›
                    </span>
                </div>
            </button>

            {popupOpen && (
                <Popup onClose={() => setPopupOpen(false)}>
                    {/* <div className="p-4 min-w-[300px]"> */}
                    <div className="p-4 min-w-[225px] md:min-w-[300px]">
                        <div className="mb-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Select Industry
                            </label>
                            <select 
                                value={selectedIndustry?.id ?? ''} 
                                onChange={handleInvestorTypeChange}
                                className="w-full p-2 border border-[#BC8B20] rounded focus:outline-none bg-white"
                            >
                                <option value="">All</option>
                                {IndustryOptions && IndustryOptions.length > 0 ? (
                                    IndustryOptions.map((industry) => (
                                        <option key={industry.id} value={industry.id}>
                                            {industry.name}
                                        </option>
                                    ))
                                ) : (
                                    <option disabled>No results found!</option>
                                )}
                            </select>
                        </div>
                    </div>
                </Popup>
            )}
        </div>
    );
}

export default SelectIndustryFilter;